import {Breakpoints, buttonBaseClasses, buttonClasses, ThemeOptions} from '@mui/material';
import {CSSProperties} from 'react';

import {Tokens} from '../../../../types/generated/Tokens';

export const MuiButtons = ({button}: Tokens, mediaQueries: Breakpoints): ThemeOptions['components'] => {
  const {base, primary, secondary, tertiary, textbtn, primaryHc, secondaryHc} = button;

  const sizeSmallDefault = {
    fontSize: base.desktop.small.font.size,
    lineHeight: base.desktop.small.font.lineHeight,
    fontWeight: base.desktop.small.font.weight,
    maxHeight: base.desktop.small.height.max,
    padding: `${base.desktop.small.padding.top} ${base.desktop.small.padding.right}`
  };

  return {
    MuiButtonBase: {
      styleOverrides: {
        root: {
          [`&.${buttonBaseClasses.focusVisible}`]: {
            boxShadow: 'none',
            outlineOffset: base.desktop.focus.outline.offset,
            outlineWidth: base.desktop.focus.outline.width,
            outlineStyle: base.desktop.focus.outline.style,
            outlineColor: base.desktop.focus.outline.color
          }
        }
      }
    },
    MuiButton: {
      defaultProps: {
        variant: 'contained',
        color: 'primary',
        disableFocusRipple: true
      },
      styleOverrides: {
        root: {
          borderBottomLeftRadius: base.desktop.radius.bottomLeft,
          borderBottomRightRadius: base.desktop.radius.bottomRight,
          borderTopLeftRadius: base.desktop.radius.topLeft,
          borderTopRightRadius: base.desktop.radius.topRight,
          padding: `${base.desktop.padding.top} ${base.desktop.padding.right} ${base.desktop.padding.bottom} ${base.desktop.padding.left}`,
          textTransform: 'none',
          [`&.${buttonClasses.disabled}`]: {
            backgroundColor: base.desktop.disabled.background.color,
            borderColor: base.desktop.disabled.border.color,
            color: base.desktop.disabled.font.color
          },
          [mediaQueries.down('md')]: {
            padding: `${base.mobile.padding.top} ${base.mobile.padding.right} ${base.mobile.padding.bottom} ${base.mobile.padding.left}`
          }
        },
        sizeSmall: {
          ...sizeSmallDefault
        },
        /* Styles applied to the root element if `variant="contained"`. */
        contained: {
          /* reset MUI defaults */
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none'
          },
          [`&.${buttonClasses.focusVisible}`]: {
            boxShadow: 'none'
          },
          '&:active': {
            boxShadow: 'none'
          },
          [`&.${buttonClasses.disabled}`]: {
            boxShadow: 'none',
            backgroundColor: base.desktop.disabled.background.color,
            borderColor: base.desktop.disabled.border.color,
            color: base.desktop.disabled.font.color
          }
        },
        /* Primary - Styles applied to the root element if `variant="contained"` and `color="primary"`. */
        containedPrimary: {
          backgroundColor: primary.desktop.background.color,
          borderRadius: primary.desktop.border.radius.all,
          borderColor: primary.desktop.border.color,
          borderStyle: primary.desktop.border.style,
          borderWidth: primary.desktop.border.width,
          color: primary.desktop.font.color,
          fontSize: primary.desktop.font.size,
          fontWeight: primary.desktop.font.weight as CSSProperties['fontWeight'],
          lineHeight: primary.desktop.font.lineHeight,
          maxHeight: primary.desktop.height.max,
          '&:hover': {
            backgroundColor: primary.desktop.hover.background.color,
            borderColor: primary.desktop.hover.border.color,
            borderStyle: primary.desktop.hover.border.style,
            borderWidth: primary.desktop.hover.border.width,
            color: primary.desktop.hover.font.color
          },
          '&:active': {
            backgroundColor: primary.desktop.active.background.color,
            borderColor: primary.desktop.active.border.color,
            boxShadow: 'none',
            color: primary.desktop.active.font.color
          },
          [mediaQueries.down('md')]: {
            fontSize: primary.mobile.font.size,
            lineHeight: primary.mobile.font.lineHeight,
            fontWeight: primary.mobile.font.weight as CSSProperties['fontWeight'],
            maxHeight: primary.mobile.height.max,
            padding: `${primary.mobile.padding.top} ${primary.mobile.padding.right} ${primary.mobile.padding.bottom} ${primary.mobile.padding.left}`
          },
          /* High Contrast Primary Button -
           * Styles applied to the root element if `variant="contained"` and `color="primary"`.
           * AND `className ="highContrastPrimary"`
           */
          '&.highContrastPrimary': {
            backgroundColor: primaryHc.desktop.background.color,
            borderRadius: primaryHc.desktop.border.radius.all,
            borderColor: primaryHc.desktop.border.color,
            borderStyle: primaryHc.desktop.border.style,
            borderWidth: primaryHc.desktop.border.width,
            color: primaryHc.desktop.font.color,
            '&:hover': {
              backgroundColor: primaryHc.desktop.hover.background.color,
              borderColor: primaryHc.desktop.hover.border.color,
              color: primaryHc.desktop.hover.font.color
            },
            '&:active': {
              backgroundColor: primaryHc.desktop.active.background.color,
              borderColor: primaryHc.desktop.active.border.color,
              color: primaryHc.desktop.active.font.color
            },
            '&.Mui-focusVisible': {
              outlineColor: `#FFF`
            }
          }
        },
        /* Secondary - Styles applied to the root element if `variant="contained"` and `color="secondary"`. */
        containedSecondary: {
          backgroundColor: secondary.desktop.background.color,
          borderRadius: secondary.desktop.border.radius.all,
          borderColor: secondary.desktop.border.color,
          borderStyle: secondary.desktop.border.style,
          borderWidth: secondary.desktop.border.width,
          color: secondary.desktop.font.color,
          fontSize: secondary.desktop.font.size,
          fontWeight: secondary.desktop.font.weight as CSSProperties['fontWeight'],
          lineHeight: secondary.desktop.font.lineHeight,
          maxHeight: secondary.desktop.height.max,
          padding: secondary.desktop.padding?.all,
          '&:hover': {
            backgroundColor: secondary.desktop.hover.background.color,
            borderColor: secondary.desktop.hover.border.color,
            borderStyle: secondary.desktop.hover.border.style,
            borderWidth: secondary.desktop.hover.border.width,
            color: secondary.desktop.hover.font.color
          },
          '&:active': {
            backgroundColor: secondary.desktop.active.background.color,
            borderColor: secondary.desktop.active.border.color,
            color: secondary.desktop.active.font.color
          },
          [mediaQueries.down('md')]: {
            fontSize: secondary.mobile.font.size,
            lineHeight: secondary.mobile.font.lineHeight,
            fontWeight: secondary.mobile.font.weight as CSSProperties['fontWeight'],
            maxHeight: secondary.mobile.height.max,
            padding: `${secondary.mobile.padding.top} ${secondary.mobile.padding.right} ${secondary.mobile.padding.bottom} ${secondary.mobile.padding.left}`
          }
        },
        /* Tertiary - Styles applied to the root element if `variant="outlined"` and `color="info"`. */
        outlinedInfo: {
          backgroundColor: tertiary.desktop.background.color,
          borderRadius: tertiary.desktop.border.radius.all,
          borderColor: tertiary.desktop.border.color,
          borderStyle: tertiary.desktop.border.style,
          borderWidth: tertiary.desktop.border.width,
          color: tertiary.desktop.font.color,
          fontSize: tertiary.desktop.font.size,
          fontWeight: tertiary.desktop.font.weight as CSSProperties['fontWeight'],
          lineHeight: tertiary.desktop.font.lineHeight,
          maxHeight: tertiary.desktop.height.max,
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: tertiary.desktop.hover.background.color,
            borderColor: tertiary.desktop.hover.border.color,
            borderStyle: tertiary.desktop.hover.border.style,
            borderWidth: tertiary.desktop.hover.border.width,
            color: tertiary.desktop.hover.font.color
          },
          '&:active': {
            backgroundColor: tertiary.desktop.active.background.color,
            borderColor: tertiary.desktop.active.border.color,
            color: tertiary.desktop.active.font.color
          },
          [mediaQueries.down('md')]: {
            fontSize: tertiary.mobile.font.size,
            lineHeight: tertiary.mobile.font.lineHeight,
            fontWeight: tertiary.mobile.font.weight as CSSProperties['fontWeight'],
            maxHeight: tertiary.mobile.height.max,
            padding: `${tertiary.mobile.padding.top} ${tertiary.mobile.padding.right} ${tertiary.mobile.padding.bottom} ${tertiary.mobile.padding.left}`
          }
        },
        /* Secondary (former variant for secondaryContained) -
         * Styles applied to the root element if `variant="outlined"` and `color="secondary"`. */
        outlinedPrimary: {
          backgroundColor: secondary.desktop.background.color,
          borderRadius: secondary.desktop.border.radius.all,
          borderColor: secondary.desktop.border.color,
          borderStyle: secondary.desktop.border.style,
          borderWidth: secondary.desktop.border.width,
          color: secondary.desktop.font.color,
          fontSize: secondary.desktop.font.size,
          fontWeight: secondary.desktop.font.weight as CSSProperties['fontWeight'],
          lineHeight: secondary.desktop.font.lineHeight,
          maxHeight: secondary.desktop.height.max,
          padding: secondary.desktop.padding?.all,
          '&:hover': {
            backgroundColor: secondary.desktop.hover.background.color,
            borderColor: secondary.desktop.hover.border.color,
            borderStyle: secondary.desktop.hover.border.style,
            borderWidth: secondary.desktop.hover.border.width,
            color: secondary.desktop.hover.font.color
          },
          '&:active': {
            backgroundColor: secondary.desktop.active.background.color,
            borderColor: secondary.desktop.active.border.color,
            color: secondary.desktop.active.font.color
          }
        },
        /* High Contrast Secondary Button -
         * Styles applied to the root element if `variant="outlined"` and `color="secondary"`.
         * AND `className="highContrastSecondary"`
         */
        outlinedSecondary: {
          '&.highContrastSecondary': {
            backgroundColor: secondaryHc.desktop.background.color,
            borderRadius: secondaryHc.desktop.border.radius.all,
            borderColor: secondaryHc.desktop.border.color,
            borderStyle: secondaryHc.desktop.border.style,
            borderWidth: secondaryHc.desktop.border.width,
            color: secondaryHc.desktop.font.color,
            '&:hover': {
              backgroundColor: secondaryHc.desktop.hover.background.color,
              borderColor: secondaryHc.desktop.hover.border.color,
              borderStyle: secondaryHc.desktop.hover.border.style,
              borderWidth: secondaryHc.desktop.hover.border.width,
              color: secondaryHc.desktop.hover.font.color
            },
            '&:active': {
              backgroundColor: secondaryHc.desktop.active.background.color,
              borderColor: secondaryHc.desktop.active.border.color,
              color: secondaryHc.desktop.active.font.color
            },
            '&.Mui-focusVisible': {
              outlineColor: '#FFF'
            }
          }
        },
        containedSizeSmall: {
          ...sizeSmallDefault,
          [mediaQueries.down('md')]: {
            ...sizeSmallDefault
          }
        },
        outlinedSizeSmall: {
          ...sizeSmallDefault,
          [mediaQueries.down('md')]: {
            ...sizeSmallDefault
          }
        },
        iconSizeSmall: {
          '& > *:nth-of-type(1)': {
            fontSize: 20
          }
        },
        /* Styles applied to the icon element if supplied and `size="medium"`. */
        iconSizeMedium: {
          '& > *:nth-of-type(1)': {
            fontSize: 24
          }
        },
        /* Styles applied to the icon element if supplied and `size="large"`. */
        iconSizeLarge: {
          '& > *:nth-of-type(1)': {
            fontSize: 24
          }
        },
        /* Styles applied to the root element if `variant="text"`. */
        text: {
          [mediaQueries.up('xs')]: {
            padding: textbtn.desktop.padding.all,
            lineHeight: textbtn.desktop.font.lineHeight,
            fontSize: textbtn.desktop.font.size,
            fontWeight: textbtn.desktop.font.weight as CSSProperties['fontWeight'],
            '&.MuiButton-fullWidth': {
              width: 'auto'
            },
            '& .MuiButton-endIcon ': {
              marginLeft: 2
            },
            '& .MuiButton-startIcon ': {
              marginRight: 2
            }
          }
        },
        /* Styles applied to the root element if `variant="text"` and `color="primary"`. */
        textPrimary: {
          color: textbtn.desktop.font.color,
          '&:hover': {
            backgroundColor: textbtn.desktop.hover.background.color,
            borderRadius: textbtn.desktop.hover.border.radius.all,
            color: textbtn.desktop.hover.font.color
          },
          '& .MuiTouchRipple-root': {
            display: 'none'
          }
        },
        /* Styles applied to the root element if `variant="text"` and `color="secondary"`. */
        textSecondary: {
          color: textbtn.desktop.font.color,
          '&:hover': {
            backgroundColor: textbtn.desktop.hover.background.color,
            color: textbtn.desktop.hover.font.color
          }
        },
        /* Styles applied to the root element if `size="small"` and `variant="text"`. */
        textSizeSmall: {
          padding: textbtn.desktop.padding.all, // override mui default
          fontSize: textbtn.desktop.small.font.size,
          [mediaQueries.down('md')]: {
            padding: textbtn.desktop.padding.all, // override mui default
            fontSize: textbtn.desktop.small.font.size
          }
        },
        /* Styles applied to the root element if `size="large"` and `variant="text"`. */
        textSizeLarge: {
          padding: textbtn.desktop.padding.all, // override mui default
          fontSize: textbtn.desktop.font.size
        }
      }
    }
  };
};
